import { useGetRoomViewScheduledSurgeries } from './hooks/surgeries.hook'
import { useEffect, useState } from 'react'
import { useGetRoomViewRooms } from './hooks/rooms.hook'
import { useGetPatientStatusOptions } from 'lib/services/restApi/organizations/organizations'
import { useAuth0 } from 'app/Auth'
import { useUser } from 'app/User'
import { useGetSurgeryStaff } from './hooks/surgeryStaff.hook'

import { usePatientStatusSocket } from './hooks/patientStatusSocket.hook'
import { useRoomViewStore } from 'store'
import { useGetAppConfig } from 'lib/apollo/hooks'
import dayjs from 'lib/dayjs'

export const useRoomViewLogic = () => {
  const { accessToken } = useAuth0()
  const { user } = useUser()

  const [isWaitingRoomHidden, setIsWaitingRoomHidden] = useState(false)
  const [dischargedPatientsModalOpen, setDischargedPatientsModalOpen] =
    useState(false)

  usePatientStatusSocket({
    orgId: user?.orgId,
    authToken: accessToken || '',
  })

  const {
    setRefetchAppConfig,
    setRefetchSurgeryStaff,
    setRefetchRoomViewRooms,
    setScheduledSurgeriesAndRooms,
    setRefetchScheduledSurgeries,
    setRefetchPatientStatusOptions,
    setAllowPatientStatusRestrictedActions,
  } = useRoomViewStore()

  const startOfDay = dayjs().startOf('day').toDate()
  const endOfDay = dayjs().endOf('day').toDate()

  const {
    data: scheduledSurgeries,
    loading: isLoadingScheduledSurgeries,
    error: scheduledSurgeriesError,
    refetch: refetchScheduledSurgeries,
  } = useGetRoomViewScheduledSurgeries({
    input: {
      startOfDay,
      endOfDay,
    },
  })

  const {
    data: roomViewRoomsData,
    loading: isLoadingRoomViewRooms,
    error: roomViewRoomsError,
    refetch: refetchRoomViewRooms,
  } = useGetRoomViewRooms({
    input: {
      startOfDay,
      endOfDay,
    },
  })

  const {
    data: patientStatusOptions,
    refetch: refetchPatientStatusOptions,
    isLoading: isLoadingPatientStatusOptions,
    error: patientStatusOptionsError,
  } = useGetPatientStatusOptions(accessToken)

  const {
    data: appConfigData,
    refetch: refetchAppConfig,
    loading: isLoadingAppConfig,
    error: appConfigError,
  } = useGetAppConfig()

  const {
    data: surgeryStaff,
    refetch: refetchSurgeryStaff,
    loading: isLoadingSurgeryStaff,
    error: surgeryStaffError,
  } = useGetSurgeryStaff()

  useEffect(() => {
    if (
      scheduledSurgeries?.getRoomViewScheduledSurgeries &&
      roomViewRoomsData?.getRoomViewRooms &&
      patientStatusOptions?.data &&
      surgeryStaff?.getSurgeryStaff &&
      appConfigData?.getAppConfig?.roomViewSettings &&
      appConfigData?.getAppConfig?.featureFlags?.hasOwnProperty(
        'allowPatientStatusRestrictedActions'
      )
    ) {
      setScheduledSurgeriesAndRooms(
        scheduledSurgeries.getRoomViewScheduledSurgeries.sort((a, b) =>
          a.patient.firstName.localeCompare(b.patient.firstName)
        ),
        roomViewRoomsData.getRoomViewRooms,
        patientStatusOptions?.data,
        surgeryStaff?.getSurgeryStaff,
        appConfigData?.getAppConfig?.roomViewSettings,
        appConfigData?.getAppConfig?.featureFlags
          ?.allowPatientStatusRestrictedActions
      )
    }
  }, [
    patientStatusOptions?.data,
    surgeryStaff?.getSurgeryStaff,
    roomViewRoomsData?.getRoomViewRooms,
    scheduledSurgeries?.getRoomViewScheduledSurgeries,
    appConfigData?.getAppConfig?.roomViewSettings,
    appConfigData?.getAppConfig?.featureFlags,
    setScheduledSurgeriesAndRooms,
    setAllowPatientStatusRestrictedActions,
  ])

  const isLoading =
    isLoadingScheduledSurgeries ||
    isLoadingRoomViewRooms ||
    isLoadingPatientStatusOptions ||
    isLoadingSurgeryStaff ||
    isLoadingAppConfig

  const error =
    scheduledSurgeriesError ||
    roomViewRoomsError ||
    patientStatusOptionsError ||
    surgeryStaffError ||
    appConfigError

  useEffect(() => {
    setRefetchScheduledSurgeries(refetchScheduledSurgeries)
    setRefetchRoomViewRooms(refetchRoomViewRooms)
    setRefetchPatientStatusOptions(refetchPatientStatusOptions)
    setRefetchSurgeryStaff(refetchSurgeryStaff)
    setRefetchAppConfig(refetchAppConfig)
  }, [
    refetchPatientStatusOptions,
    refetchRoomViewRooms,
    refetchScheduledSurgeries,
    refetchSurgeryStaff,
    setRefetchPatientStatusOptions,
    setRefetchRoomViewRooms,
    setRefetchScheduledSurgeries,
    setRefetchSurgeryStaff,
    setRefetchAppConfig,
    refetchAppConfig,
  ])

  return {
    error,
    isLoading,
    isWaitingRoomHidden,
    dischargedPatientsModalOpen,
    setIsWaitingRoomHidden,
    setDischargedPatientsModalOpen,
  }
}
